import React, { useState, useMemo, useEffect } from 'react';
import { 
  ShoppingBag, 
  CheckCircle2, 
  AlertCircle, 
  Sparkles, 
  Plus, 
  Minus, 
  Check, 
  ShieldCheck, 
  Heart, 
  Share2, 
  Info, 
  ChevronDown, 
  ChevronUp,
  Star,
  MessageSquare,
  Send,
  User,
  MapPin,
  Truck,
  ArrowRight,
  BookOpen,
  Tag,
  Copy,
  MessageCircle,
  Clock,
  Layers,
  Award
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';
import { useCart } from '../../context/CartContext';
import { formatPrice, formatMizajDisplay } from '../../utils/formatters';
import { sortVariantsByWeight } from '../../utils/weightPricing';
import { getProductAltText } from '../../utils/seoHelpers';
import ProductCard from './ProductCard';

const RATING_LABELS_UR = {
  5: 'بہترین اور انتہائی مفید (5/5)',
  4: 'بہت اچھا رزلٹ (4/5)',
  3: 'مناسب و تسلی بخش (3/5)',
  2: 'عام نتائج (2/5)',
  1: 'مطمئن نہیں (1/5)'
};

const RATING_LABELS_EN = {
  5: 'Excellent & Highly Effective (5/5)',
  4: 'Very Good Results (4/5)',
  3: 'Satisfactory (3/5)',
  2: 'Average Results (2/5)',
  1: 'Not Satisfied (1/5)'
};

const ProductDetailPage = ({ 
  product: propProduct, 
  onBackToHome, 
  onBackToShop, 
  onSelectProduct,
  onSelectArticle
}) => {
  const { lang, t, isRtl } = useLanguage();
  const { 
    settings, 
    products, 
    categories, 
    mizajList, 
    reviews, 
    addReview, 
    getProductReviews, 
    getProductRatingStats, 
    orders,
    blogs 
  } = useStore();
  
  const { 
    selectedProduct: contextProduct,
    addToCart, 
    setIsCartOpen, 
    setIsCheckoutOpen 
  } = useCart();

  const product = propProduct || contextProduct;

  // Multi-image gallery state
  const allImages = useMemo(() => {
    if (!product) return [];
    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      return product.images;
    }
    return [product.image];
  }, [product]);

  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    setActiveImageIndex(0);
  }, [product?.id]);

  // Active Tab for detailed info: 'benefits', 'usage', 'mizaj', 'reviews'
  const [activeTab, setActiveTab] = useState('benefits');

  // Review Form State
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    customerName: '',
    city: '',
    orderId: '',
    rating: 5,
    comment: ''
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Auto-detect matching order if customer enters an Order ID
  const matchedOrder = useMemo(() => {
    if (!reviewForm.orderId || !orders || orders.length === 0) return null;
    const cleanId = reviewForm.orderId.trim().toUpperCase();
    return orders.find(o => o.id?.toUpperCase() === cleanId || o.id?.toUpperCase() === `HP-${cleanId}`);
  }, [reviewForm.orderId, orders]);

  // Compute live rating & reviews
  const ratingStats = useMemo(() => {
    if (!product) return { rating: 5.0, count: 0 };
    return getProductRatingStats 
      ? getProductRatingStats(product.id, product.rating || 4.9, product.reviewsCount || 24) 
      : { rating: product.rating || 4.9, count: product.reviewsCount || 24 };
  }, [product, reviews, getProductRatingStats]);

  const productReviews = useMemo(() => {
    if (!product) return [];
    return getProductReviews ? getProductReviews(product.id) : [];
  }, [product, reviews, getProductReviews]);

  const handleReviewSubmit = (e) => {
    e.preventDefault();
    if (!reviewForm.comment.trim() || !reviewForm.customerName.trim()) return;

    addReview({
      productId: product.id,
      customerName: reviewForm.customerName,
      city: reviewForm.city,
      orderId: reviewForm.orderId,
      rating: reviewForm.rating,
      comment: reviewForm.comment
    });

    setReviewSubmitted(true);
    setReviewForm({
      customerName: '',
      city: '',
      orderId: '',
      rating: 5,
      comment: ''
    });
    setTimeout(() => {
      setReviewSubmitted(false);
      setShowReviewForm(false);
    }, 2500);
  };

  // Sort variants ascending by weight (50g -> 100g -> 250g -> 500g -> 750g -> 1kg)
  const sortedVariants = useMemo(() => {
    return sortVariantsByWeight(product?.variants || []);
  }, [product]);

  const [selectedVariant, setSelectedVariant] = useState(null);

  useEffect(() => {
    if (sortedVariants.length > 0) {
      setSelectedVariant(sortedVariants[0]);
    } else {
      setSelectedVariant(null);
    }
    setQuantity(1);
    setActiveTab('benefits');
  }, [product?.id]);

  const [quantity, setQuantity] = useState(1);
  const [addedAnimation, setAddedAnimation] = useState(false);

  // Category object
  const currentCategory = useMemo(() => {
    if (!product?.category || !categories) return null;
    return categories.find(c => c.id === product.category);
  }, [product, categories]);

  // Related products (same category or similar, excluding current)
  const relatedProducts = useMemo(() => {
    if (!product || !products) return [];
    return products
      .filter(p => p.id !== product.id && (p.category === product.category || p.mizaj === product.mizaj))
      .slice(0, 4);
  }, [product, products]);

  // Related blog / health article (if any)
  const relatedBlog = useMemo(() => {
    if (!product || !blogs) return null;
    return blogs.find(b => 
      b.status === 'published' && 
      (b.linkedProductId === product.id || 
       (b.focusKeywords && b.focusKeywords.includes(product.nameUrdu.split(' ')[0])) ||
       (b.title && b.title.includes(product.nameUrdu.split(' ')[0]))
      )
    );
  }, [product, blogs]);

  if (!product) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <div className="bg-white rounded-3xl p-8 sm:p-12 border border-gray-100 shadow-sm">
          <div className="w-16 h-16 bg-pansar-50 text-pansar-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ShoppingBag className="w-8 h-8" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold font-title text-gray-900 mb-2">
            {lang === 'ur' ? 'کوئی دوا یا پروڈکٹ منتخب نہیں کی گئی' : 'No Product Selected'}
          </h2>
          <p className="text-gray-500 mb-6 text-xs sm:text-sm font-urdu">
            {lang === 'ur' ? 'براہ کرم اسٹور یا سرچ بار سے کسی دوا کا انتخاب کریں۔' : 'Please select a product from the shop catalog or search bar.'}
          </p>
          <button
            onClick={onBackToShop || onBackToHome}
            className="px-6 py-3 bg-pansar-700 hover:bg-pansar-800 text-white rounded-xl font-bold text-sm font-urdu transition-all shadow-md cursor-pointer"
          >
            {lang === 'ur' ? 'تمام ادویات دیکھیں' : 'View All Products'}
          </button>
        </div>
      </div>
    );
  }

  const currentPrice = selectedVariant ? selectedVariant.price : (sortedVariants[0]?.price || 0);
  const mizajDisplay = formatMizajDisplay(product, mizajList, lang);
  const ratingLabels = lang === 'ur' ? RATING_LABELS_UR : RATING_LABELS_EN;

  const handleAddToCart = () => {
    addToCart(product, selectedVariant, quantity);
    setAddedAnimation(true);
    setTimeout(() => setAddedAnimation(false), 1500);
  };

  const handleBuyNow = () => {
    addToCart(product, selectedVariant, quantity);
    if (setIsCheckoutOpen) {
      setIsCheckoutOpen(true);
    }
  };

  const handleCopyProductLink = () => {
    const url = window.location.origin + window.location.pathname + `?product=${product.id}`;
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  return (
    <div className={`min-h-screen bg-gray-50/70 pb-20 pt-4 sm:pt-6 ${lang === 'ur' ? 'font-urdu' : 'font-sans'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        
        {/* Breadcrumb Navigation Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-3.5 sm:p-4 rounded-2xl border border-gray-200/80 shadow-xs text-xs sm:text-sm">
          <nav className="flex items-center gap-1.5 sm:gap-2 text-gray-500 overflow-x-auto whitespace-nowrap scrollbar-none">
            <button
              onClick={onBackToHome}
              className="hover:text-emerald-800 font-bold transition flex items-center gap-1 cursor-pointer"
            >
              <span>{t('home')}</span>
            </button>
            <span className="text-gray-300">/</span>
            <button
              onClick={onBackToShop}
              className="hover:text-emerald-800 font-bold transition cursor-pointer"
            >
              <span>{t('shop')}</span>
            </button>
            {currentCategory && (
              <>
                <span className="text-gray-300">/</span>
                <span className="text-emerald-800 font-medium">
                  {lang === 'ur' ? currentCategory.nameUrdu : currentCategory.nameEn}
                </span>
              </>
            )}
            <span className="text-gray-300">/</span>
            <span className="text-gray-900 font-bold truncate max-w-[200px] sm:max-w-none">
              {lang === 'ur' ? product.nameUrdu : (product.nameEn || product.nameUrdu)}
            </span>
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyProductLink}
              title={lang === 'ur' ? 'پروڈکٹ کا لنک کاپی کریں' : 'Copy product link'}
              className="p-2 rounded-xl bg-gray-100 hover:bg-emerald-50 hover:text-emerald-800 text-gray-600 transition flex items-center gap-1 text-xs cursor-pointer"
            >
              {copiedLink ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              <span className="hidden sm:inline">
                {copiedLink 
                  ? (lang === 'ur' ? 'کاپی ہو گیا' : 'Copied!') 
                  : (lang === 'ur' ? 'لنک کاپی کریں' : 'Copy Link')}
              </span>
            </button>

            <button
              onClick={onBackToShop}
              className="inline-flex items-center gap-1 text-xs font-bold text-emerald-800 hover:text-emerald-950 bg-emerald-50 px-3 py-1.5 rounded-xl border border-emerald-200 transition cursor-pointer"
            >
              {isRtl ? <ArrowRight className="w-3.5 h-3.5" /> : <ArrowRight className="w-3.5 h-3.5 rotate-180" />}
              <span>{lang === 'ur' ? 'واپس اسٹور' : 'Back to Store'}</span>
            </button>
          </div>
        </div>

        {/* Main Product Showcase Box (Grid 12 Cols) */}
        <div className="bg-white rounded-3xl p-5 sm:p-8 lg:p-10 border border-gray-200/90 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            
            {/* Left Column: Multi-Image Gallery (5 cols) */}
            <div className="lg:col-span-5 space-y-4">
              
              {/* Main Featured Image Display */}
              <div className="relative aspect-square rounded-3xl overflow-hidden bg-emerald-950/5 border border-gray-200/80 shadow-md group">
                <img
                  src={allImages[activeImageIndex] || product.image}
                  alt={getProductAltText(product, activeImageIndex, lang)}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />

                {/* Stock Badge Overlay */}
                <div className="absolute top-4 right-4 flex flex-col gap-2">
                  {product.inStock ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-600/95 text-white text-xs font-bold shadow-md backdrop-blur-xs">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>{t('inStock')}</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-rose-600 text-white text-xs font-bold shadow-md">
                      <AlertCircle className="w-3.5 h-3.5" />
                      <span>{t('outOfStock')}</span>
                    </span>
                  )}
                </div>

                {/* Pure Herbs Guarantee Badge */}
                <div className="absolute bottom-4 right-4">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-black/60 text-amber-300 text-[11px] font-bold backdrop-blur-xs border border-amber-400/30">
                    <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                    <span>{lang === 'ur' ? '100% خالص قدرتی دیسی دوا' : '100% Pure Natural Herb'}</span>
                  </span>
                </div>
              </div>

              {/* Thumbnails Gallery Strip (If multiple images exist) */}
              {allImages.length > 1 && (
                <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-none">
                  {allImages.map((imgSrc, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveImageIndex(idx)}
                      className={`w-18 h-18 sm:w-20 sm:h-20 rounded-2xl overflow-hidden border-2 transition-all p-0.5 shrink-0 cursor-pointer ${
                        activeImageIndex === idx 
                          ? 'border-emerald-700 ring-2 ring-emerald-600/30 shadow-md scale-105' 
                          : 'border-gray-200 hover:border-emerald-400 opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img 
                        src={imgSrc} 
                        alt={getProductAltText(product, idx, lang)} 
                        className="w-full h-full object-cover rounded-xl"
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* Clinic & Fast Delivery Trust Card */}
              <div className="bg-emerald-50/60 rounded-2xl p-4 border border-emerald-200/60 space-y-2.5 text-xs text-emerald-950">
                <div className="flex items-center gap-2.5 font-bold">
                  <Truck className="w-4 h-4 text-emerald-700 shrink-0" />
                  <span>{lang === 'ur' ? 'پورے پاکستان کے تمام شہروں میں کیش آن ڈلیوری کی سہولت' : 'Cash on Delivery available all across Pakistan'}</span>
                </div>
                <div className="flex items-center gap-2.5 font-bold">
                  <ShieldCheck className="w-4 h-4 text-emerald-700 shrink-0" />
                  <span>{lang === 'ur' ? 'حکیم مولانا سید عبدالوہاب شاہ شیرازی کی نگرانی میں تیار کردہ' : 'Prepared under supervision of Hakim Syed Abdul Wahab Shah Shirazi'}</span>
                </div>
                <div className="flex items-center gap-2.5 font-bold">
                  <Award className="w-4 h-4 text-amber-600 shrink-0" />
                  <span>{lang === 'ur' ? 'خالص اجزاء، بغیر کسی نقصان دہ کیمیکل یا اسٹیرائیڈ کے' : '100% Pure Herbs, Free from Steroids & Harmful Chemicals'}</span>
                </div>
              </div>

            </div>

            {/* Right Column: Buying Options & Product Details (7 cols) */}
            <div className="lg:col-span-7 space-y-6 text-start">
              
              {/* Category & Mizaj Pill */}
              <div className="flex flex-wrap items-center gap-2">
                {currentCategory && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 text-emerald-900 text-xs font-bold border border-emerald-200">
                    <Tag className="w-3.5 h-3.5 text-emerald-700" />
                    <span>{lang === 'ur' ? currentCategory.nameUrdu : currentCategory.nameEn}</span>
                  </span>
                )}
                {mizajDisplay && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-bold border border-amber-200">
                    <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                    <span>{lang === 'ur' ? `مزاج: ${mizajDisplay}` : `Mizaj: ${mizajDisplay}`}</span>
                  </span>
                )}
              </div>

              {/* Product Title */}
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 leading-tight">
                  {lang === 'ur' ? product.nameUrdu : (product.nameEn || product.nameUrdu)}
                </h1>
                {product.nameEn && lang === 'ur' && (
                  <p className="text-sm sm:text-base text-gray-500 font-sans mt-1">
                    {product.nameEn}
                  </p>
                )}
                {product.nameUrdu && lang === 'en' && (
                  <p className="text-sm sm:text-base text-gray-500 font-urdu mt-1">
                    {product.nameUrdu}
                  </p>
                )}
              </div>

              {/* Star Rating Display */}
              <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      className={`w-4 h-4 sm:w-5 sm:h-5 ${
                        s <= Math.round(ratingStats.rating)
                          ? 'text-amber-500 fill-amber-500'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-xs sm:text-sm font-bold text-gray-700 font-sans">
                  {ratingStats.rating} / 5.0
                </span>
                <span className="text-xs text-gray-400">
                  {lang === 'ur' 
                    ? `(${ratingStats.count} کسٹمر ریویوز و تصدیق شدہ رائے)` 
                    : `(${ratingStats.count} Customer Reviews & Feedback)`}
                </span>
              </div>

              {/* Price Display */}
              <div className="bg-pansar-50/80 rounded-2xl p-4 sm:p-5 border border-pansar-200/70 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <span className="text-xs text-gray-500 block mb-0.5">
                    {lang === 'ur' ? 'قیمت برائے منتخب وزن:' : 'Price for selected weight:'}
                  </span>
                  <div className="text-2xl sm:text-3xl font-black text-pansar-900 font-sans">
                    {formatPrice(currentPrice, lang)}
                  </div>
                </div>

                {selectedVariant && (
                  <div>
                    <span className="px-3 py-1 rounded-full bg-emerald-800 text-amber-300 font-bold text-xs shadow-xs">
                      {lang === 'ur' ? selectedVariant.weight : (selectedVariant.weightEn || selectedVariant.weight)}
                    </span>
                  </div>
                )}
              </div>

              {/* Weight / Volume Variant Selector */}
              {sortedVariants.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs sm:text-sm font-bold text-gray-800">
                      {t('selectWeight')}
                    </label>
                    <span className="text-[11px] text-gray-400">
                      {lang === 'ur' ? '(بڑے وزن پر خصوصی رعایت شامل ہے)' : '(Bulk discount included)'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    {sortedVariants.map((v, i) => {
                      const isSelected = selectedVariant?.weight === v.weight;
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setSelectedVariant(v)}
                          className={`p-3 rounded-2xl border text-start transition cursor-pointer flex flex-col justify-between ${
                            isSelected
                              ? 'bg-emerald-900 text-white border-emerald-900 shadow-md ring-2 ring-emerald-600/30'
                              : 'bg-white hover:bg-emerald-50 text-gray-800 border-gray-200'
                          }`}
                        >
                          <span className="text-xs font-bold leading-snug">
                            {lang === 'ur' ? v.weight : (v.weightEn || v.weight)}
                          </span>
                          <span className={`text-xs font-black font-sans mt-1.5 ${
                            isSelected ? 'text-amber-300' : 'text-emerald-800'
                          }`}>
                            {formatPrice(v.price, lang)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quantity Selector & Action Buttons */}
              <div className="space-y-4 pt-2">
                <div className="flex flex-wrap items-center gap-4">
                  
                  {/* Quantity Counter */}
                  <div className="flex items-center border-2 border-gray-200 rounded-2xl p-1 bg-gray-50">
                    <button
                      type="button"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-9 h-9 rounded-xl bg-white hover:bg-gray-200 text-gray-800 font-bold flex items-center justify-center transition cursor-pointer shadow-2xs"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-12 text-center font-black text-base font-sans text-gray-900">
                      {quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-9 h-9 rounded-xl bg-white hover:bg-gray-200 text-gray-800 font-bold flex items-center justify-center transition cursor-pointer shadow-2xs"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Add to Cart Button */}
                  <button
                    type="button"
                    onClick={handleAddToCart}
                    disabled={!product.inStock}
                    className={`flex-1 py-3.5 px-6 rounded-2xl font-bold text-sm shadow-md transition flex items-center justify-center gap-2 ${
                      !product.inStock
                        ? 'bg-rose-50 text-rose-700 border border-rose-200 cursor-not-allowed opacity-90'
                        : addedAnimation
                        ? 'bg-emerald-600 text-white cursor-pointer'
                        : 'bg-emerald-800 hover:bg-emerald-900 text-white cursor-pointer'
                    }`}
                  >
                    {addedAnimation ? (
                      <>
                        <CheckCircle2 className="w-5 h-5 text-white" />
                        <span>{lang === 'ur' ? 'کارٹ میں شامل کر دیا گیا!' : 'Added to Cart!'}</span>
                      </>
                    ) : !product.inStock ? (
                      <>
                        <AlertCircle className="w-5 h-5 text-rose-500" />
                        <span>{t('outOfStock')}</span>
                      </>
                    ) : (
                      <>
                        <ShoppingBag className="w-5 h-5" />
                        <span>{t('addToCart')}</span>
                      </>
                    )}
                  </button>

                  {/* Buy Now (Direct Checkout) Button */}
                  {product.inStock && (
                    <button
                      type="button"
                      onClick={handleBuyNow}
                      className="w-full sm:w-auto py-3.5 px-8 rounded-2xl bg-amber-500 hover:bg-amber-600 text-gray-950 font-black text-sm shadow-md transition flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <Sparkles className="w-4 h-4 text-gray-950" />
                      <span>{lang === 'ur' ? 'ابھی آرڈر کریں (Buy Now)' : 'Buy Now'}</span>
                    </button>
                  )}
                </div>
              </div>

            </div>

          </div>
        </div>

        {/* Detailed Tabs Section (Benefits, Dosage, Tibb & Mizaj, Customer Reviews) */}
        <div className="bg-white rounded-3xl border border-gray-200/90 shadow-sm overflow-hidden">
          
          {/* Tab Headers */}
          <div className="flex border-b border-gray-200 bg-gray-50/70 p-2 overflow-x-auto scrollbar-none gap-2">
            <button
              onClick={() => setActiveTab('benefits')}
              className={`py-3 px-5 rounded-2xl text-xs sm:text-sm font-bold transition whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                activeTab === 'benefits'
                  ? 'bg-emerald-900 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span>{lang === 'ur' ? 'فوائد و طبی خواص' : 'Health Benefits'}</span>
            </button>

            <button
              onClick={() => setActiveTab('usage')}
              className={`py-3 px-5 rounded-2xl text-xs sm:text-sm font-bold transition whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                activeTab === 'usage'
                  ? 'bg-emerald-900 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Clock className="w-4 h-4 text-emerald-400" />
              <span>{lang === 'ur' ? 'طریقہ استعمال و مقدار خوراک' : 'Usage & Dosage'}</span>
            </button>

            <button
              onClick={() => setActiveTab('mizaj')}
              className={`py-3 px-5 rounded-2xl text-xs sm:text-sm font-bold transition whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                activeTab === 'mizaj'
                  ? 'bg-emerald-900 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Layers className="w-4 h-4 text-emerald-400" />
              <span>{lang === 'ur' ? 'قانون مفرد اعضاء و مزاج گائیڈ' : 'Tibb & Mizaj Guide'}</span>
            </button>

            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-3 px-5 rounded-2xl text-xs sm:text-sm font-bold transition whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                activeTab === 'reviews'
                  ? 'bg-emerald-900 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span>{lang === 'ur' ? `کسٹمر ریویوز (${ratingStats.count})` : `Customer Reviews (${ratingStats.count})`}</span>
            </button>
          </div>

          {/* Tab Content Body */}
          <div className="p-6 sm:p-10 text-start leading-relaxed">
            
            {/* TAB 1: BENEFITS */}
            {activeTab === 'benefits' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                    <span>{lang === 'ur' ? 'طبی فوائد اور جسمانی اثرات' : 'Therapeutic Benefits & Health Effects'}</span>
                  </h3>
                  <div className="text-sm sm:text-base text-gray-700 leading-relaxed sm:leading-loose whitespace-pre-line bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                    {lang === 'ur'
                      ? (product.benefitsUrdu || 'یہ ایک خالص نباتاتی مرکب ہے جو جسم کے اعضاء رئیسہ (دل، دماغ، جگر) کو تقویت فراہم کرتا ہے اور قدرتی دفاعی نظام کو مضبوط بناتا ہے۔')
                      : (product.benefitsEn || product.benefitsUrdu || 'A 100% natural herbal formula that supports vital organ health and enhances immunity.')}
                  </div>
                </div>

                {product.benefitsEn && lang === 'ur' && (
                  <div className="pt-4 border-t border-gray-100 text-start">
                    <h4 className="text-sm font-bold text-gray-800 font-sans mb-1">
                      Key Health Benefits (English):
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600 font-sans leading-relaxed">
                      {product.benefitsEn}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* TAB 2: USAGE & DOSAGE */}
            {activeTab === 'usage' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-emerald-700" />
                    <span>{lang === 'ur' ? 'طریقہ استعمال اور احتیاطی تدابیر' : 'Dosage & Directions for Use'}</span>
                  </h3>
                  <div className="text-sm sm:text-base text-gray-700 leading-relaxed sm:leading-loose whitespace-pre-line bg-emerald-50/40 p-6 rounded-2xl border border-emerald-100">
                    {lang === 'ur'
                      ? (product.usageUrdu || 'معالج کی ہدایت کے مطابق نہار منہ یا بعد از طعام مناسب بدرقہ (نیم گرم پانی یا دودھ) کے ساتھ استعمال کریں۔')
                      : (product.usageEn || product.usageUrdu || 'Take with lukewarm water or milk as directed by your physician or herbal practitioner.')}
                  </div>
                </div>

                {(product.precautionsUrdu || product.precautionsEn) && (
                  <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-950 text-xs sm:text-sm">
                    <div className="font-bold flex items-center gap-1.5 mb-1 text-amber-900">
                      <AlertCircle className="w-4 h-4 text-amber-700" />
                      <span>{lang === 'ur' ? 'ضروری احتیاط:' : 'Important Precautions:'}</span>
                    </div>
                    <p>{lang === 'ur' ? product.precautionsUrdu : (product.precautionsEn || product.precautionsUrdu)}</p>
                  </div>
                )}
              </div>
            )}

            {/* TAB 3: TIBB & MIZAJ */}
            {activeTab === 'mizaj' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Layers className="w-5 h-5 text-emerald-700" />
                    <span>{lang === 'ur' ? 'قانون مفرد اعضاء اور مزاج کی تفصیل' : 'Tibb-e-Pakistani & Elementary Organ Principles'}</span>
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-5 rounded-2xl bg-emerald-50 border border-emerald-200 space-y-2">
                      <span className="text-xs text-emerald-800 font-bold block">
                        {lang === 'ur' ? 'مقررہ مزاج:' : 'Assigned Mizaj:'}
                      </span>
                      <div className="text-lg font-bold text-emerald-950">
                        {mizajDisplay || (lang === 'ur' ? 'معتدل' : 'Moderate')}
                      </div>
                      <p className="text-xs text-emerald-800 leading-relaxed">
                        {lang === 'ur'
                          ? 'یہ دوا جسم کے متاثرہ عضو میں تحریک پیدا کر کے افعال کو درست سمت میں لاتی ہے اور غیر طبعی اخلاط کو خارج کرتی ہے۔'
                          : 'This formulation stimulates targeted organ vitality according to Sabir Multani principles.'}
                      </p>
                    </div>

                    <div className="p-5 rounded-2xl bg-gray-50 border border-gray-200 space-y-2">
                      <span className="text-xs text-gray-600 font-bold block">
                        {lang === 'ur' ? 'طبی اصول صابر ملتانیؒ:' : 'Sabir Multani Principles:'}
                      </span>
                      <p className="text-xs text-gray-700 leading-relaxed">
                        {lang === 'ur'
                          ? 'دوا کا استعمال مرض کے اصل سبب (تحریک، تسکین، تحلیل) کے مطابق کیا جانا چاہیے۔ کسی پیچیدہ مرض کی صورت میں حکیم صاحب سے نبض کا معائنہ یا آن لائن مشورہ ضرور لیں۔'
                          : 'Treatment is formulated according to organ tissue states (Stimulation, Sedation, Lysis). Consult Hakim Sahib for pulse diagnosis.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 4: REVIEWS */}
            {activeTab === 'reviews' && (
              <div className="space-y-8">
                
                {/* Rating Summary Header */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-6 bg-amber-50/50 p-6 rounded-3xl border border-amber-200/60">
                  <div className="text-center sm:text-start">
                    <div className="text-3xl sm:text-4xl font-black text-amber-900 font-sans">
                      {ratingStats.rating} <span className="text-lg text-amber-700">/ 5.0</span>
                    </div>
                    <div className="flex items-center justify-center sm:justify-start gap-1 my-1">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star
                          key={s}
                          className={`w-4 h-4 ${
                            s <= Math.round(ratingStats.rating)
                              ? 'text-amber-500 fill-amber-500'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-amber-900 font-medium">
                      {lang === 'ur' 
                        ? `کل ${ratingStats.count} کسٹمرز کے حقیقی تاثرات اور ریویوز` 
                        : `Based on ${ratingStats.count} verified customer reviews`}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowReviewForm(!showReviewForm)}
                    className="px-6 py-3 rounded-2xl bg-emerald-800 hover:bg-emerald-900 text-white font-bold text-xs sm:text-sm shadow-md transition cursor-pointer flex items-center gap-2"
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span>
                      {showReviewForm 
                        ? (lang === 'ur' ? 'ریویو فارم بند کریں' : 'Close Review Form') 
                        : (lang === 'ur' ? 'اپنا ریویو اور رائے درج کریں' : 'Write a Review')}
                    </span>
                  </button>
                </div>

                {/* Review Submission Form */}
                {showReviewForm && (
                  <form onSubmit={handleReviewSubmit} className="bg-white p-6 rounded-3xl border border-emerald-200 shadow-md space-y-4 animate-fade-in">
                    <h4 className="font-bold text-base text-gray-900 border-b border-gray-100 pb-3">
                      {lang === 'ur' ? 'اس پروڈکٹ کے بارے میں اپنا حقیقی تجربہ شیئر کریں' : 'Share your real experience with this product'}
                    </h4>

                    {reviewSubmitted && (
                      <div className="p-4 rounded-2xl bg-emerald-100 text-emerald-900 font-bold text-xs flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                        <span>{lang === 'ur' ? 'آپ کا ریویو موصول ہو گیا ہے اور کامیابی سے شامل کر دیا گیا ہے۔ شکریہ!' : 'Your review has been submitted successfully. Thank you!'}</span>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="text-xs font-bold text-gray-700 block mb-1">
                          {lang === 'ur' ? 'آپ کا نام *' : 'Your Name *'}
                        </label>
                        <input
                          type="text"
                          required
                          value={reviewForm.customerName}
                          onChange={(e) => setReviewForm({ ...reviewForm, customerName: e.target.value })}
                          placeholder={lang === 'ur' ? 'مثلاً: محمد عثمان' : 'e.g. John Doe'}
                          className="w-full p-2.5 rounded-xl border border-gray-200 text-xs focus:ring-2 focus:ring-emerald-600 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-700 block mb-1">
                          {lang === 'ur' ? 'شہر کا نام' : 'City'}
                        </label>
                        <input
                          type="text"
                          value={reviewForm.city}
                          onChange={(e) => setReviewForm({ ...reviewForm, city: e.target.value })}
                          placeholder={lang === 'ur' ? 'مثلاً: لاہور، راولپنڈی' : 'e.g. Islamabad, Lahore'}
                          className="w-full p-2.5 rounded-xl border border-gray-200 text-xs focus:ring-2 focus:ring-emerald-600 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-700 block mb-1">
                          {lang === 'ur' ? 'آرڈر نمبر (اختیاری)' : 'Order ID (Optional)'}
                        </label>
                        <input
                          type="text"
                          value={reviewForm.orderId}
                          onChange={(e) => setReviewForm({ ...reviewForm, orderId: e.target.value })}
                          placeholder="e.g. HP-1082"
                          className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans focus:ring-2 focus:ring-emerald-600 focus:outline-none"
                        />
                      </div>
                    </div>

                    {/* Star Rating Picker */}
                    <div>
                      <label className="text-xs font-bold text-gray-700 block mb-1.5">
                        {lang === 'ur' ? 'اسٹار ریٹنگ منتخب کریں:' : 'Select Star Rating:'}
                      </label>
                      <div className="flex items-center gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                            onMouseEnter={() => setHoverRating(star)}
                            onMouseLeave={() => setHoverRating(0)}
                            className="p-1 cursor-pointer transition hover:scale-110"
                          >
                            <Star
                              className={`w-7 h-7 ${
                                star <= (hoverRating || reviewForm.rating)
                                  ? 'text-amber-500 fill-amber-500'
                                  : 'text-gray-300'
                              }`}
                            />
                          </button>
                        ))}
                        <span className="text-xs text-gray-600 mx-2 font-bold">
                          {ratingLabels[hoverRating || reviewForm.rating]}
                        </span>
                      </div>
                    </div>

                    {/* Review Comment */}
                    <div>
                      <label className="text-xs font-bold text-gray-700 block mb-1">
                        {lang === 'ur' ? 'آپ کا تفصیلی تبصرہ و نتائج *' : 'Your Detailed Feedback *'}
                      </label>
                      <textarea
                        required
                        rows="3"
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                        placeholder={lang === 'ur' ? 'آپ نے یہ دوا کیسے استعمال کی اور کیا نتائج ملے؟' : 'How did you use this remedy and what results did you experience?'}
                        className="w-full p-3 rounded-xl border border-gray-200 text-xs focus:ring-2 focus:ring-emerald-600 focus:outline-none"
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setShowReviewForm(false)}
                        className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700 text-xs font-bold cursor-pointer"
                      >
                        {t('cancel')}
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2 rounded-xl bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold transition shadow-sm cursor-pointer"
                      >
                        {lang === 'ur' ? 'ریویو جمع کروائیں' : 'Submit Review'}
                      </button>
                    </div>
                  </form>
                )}

                {/* Reviews List */}
                <div className="space-y-4">
                  {productReviews.length > 0 ? (
                    productReviews.map((rev) => (
                      <div
                        key={rev.id}
                        className="p-5 rounded-2xl bg-gray-50/70 border border-gray-200/80 space-y-2.5 text-start"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200/60 pb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-emerald-800 text-white flex items-center justify-center font-bold text-xs">
                              {rev.customerName?.charAt(0) || 'U'}
                            </div>
                            <div>
                              <div className="font-bold text-xs sm:text-sm text-gray-900 flex items-center gap-1.5">
                                <span>{rev.customerName}</span>
                                {rev.verified && (
                                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                                    {lang === 'ur' ? '✓ تصدیق شدہ خریدار' : '✓ Verified Buyer'}
                                  </span>
                                )}
                              </div>
                              {rev.city && (
                                <span className="text-[11px] text-gray-400">
                                  {rev.city}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((st) => (
                              <Star
                                key={st}
                                className={`w-3.5 h-3.5 ${
                                  st <= (rev.rating || 5)
                                    ? 'text-amber-500 fill-amber-500'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>

                        <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                          {rev.comment}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center bg-gray-50 rounded-2xl border border-gray-200 text-gray-500 text-xs">
                      {lang === 'ur' 
                        ? 'فی الحال کوئی تفصیلی ریویو موجود نہیں۔ آپ پہلے خریدار بن کر اپنا تجربہ شیئر کریں!' 
                        : 'No reviews yet. Be the first customer to share your experience!'}
                    </div>
                  )}
                </div>

              </div>
            )}

          </div>

        </div>

        {/* Linked Health Article Box (If Available) */}
        {relatedBlog && (
          <div 
            onClick={() => onSelectArticle && onSelectArticle(relatedBlog)}
            className="bg-gradient-to-r from-emerald-900 via-emerald-850 to-pansar-900 text-white rounded-3xl p-6 sm:p-8 shadow-md flex flex-col md:flex-row items-center justify-between gap-6 cursor-pointer group border border-emerald-700 hover:border-amber-400 transition"
          >
            <div className="space-y-2 max-w-2xl text-start">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-800/90 text-amber-300 text-xs font-bold">
                <BookOpen className="w-3.5 h-3.5" />
                <span>{lang === 'ur' ? 'حکیم صاحب کا خاص معلوماتی مقالہ' : 'Special Article by Hakim Sahib'}</span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold group-hover:text-amber-200 transition">
                {lang === 'ur' ? (relatedBlog.titleUrdu || relatedBlog.title) : (relatedBlog.titleEn || relatedBlog.title)}
              </h3>
              <p className="text-xs sm:text-sm text-emerald-100 line-clamp-2">
                {lang === 'ur' ? (relatedBlog.excerptUrdu || relatedBlog.excerpt) : (relatedBlog.excerptEn || relatedBlog.excerpt)}
              </p>
            </div>

            <button
              type="button"
              className="px-6 py-3 rounded-2xl bg-amber-500 hover:bg-amber-600 text-gray-950 font-black text-xs sm:text-sm shadow-md transition shrink-0 cursor-pointer"
            >
              {lang === 'ur' ? 'مضمون پڑھیں' : 'Read Article'}
            </button>
          </div>
        )}

        {/* Related Products Section (ملتی جلتی مزید ادویات) */}
        {relatedProducts.length > 0 && (
          <div className="space-y-6 pt-4">
            <div className="flex items-center justify-between border-b border-gray-200 pb-4">
              <div className="text-start">
                <h3 className="text-xl sm:text-2xl font-black text-gray-900 font-title">
                  {lang === 'ur' ? 'ملتی جلتی مزید خالص ادویات و جڑی بوٹیاں' : 'Related Natural Herbs & Medicines'}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {lang === 'ur' ? 'اسی شعبے اور مزاج سے متعلق دیگر مقبول ادویات' : 'Other popular herbal formulations from this category and temperament'}
                </p>
              </div>

              <button
                type="button"
                onClick={onBackToShop}
                className="text-xs font-bold text-emerald-800 hover:text-emerald-950 bg-emerald-50 px-3.5 py-1.5 rounded-xl border border-emerald-200 cursor-pointer"
              >
                {lang === 'ur' ? 'تمام ادویات دیکھیں' : 'View All Products'}
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relProd) => (
                <div 
                  key={relProd.id}
                  onClick={() => {
                    if (onSelectProduct) {
                      onSelectProduct(relProd);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                >
                  <ProductCard product={relProd} />
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ProductDetailPage;
